import { useCallback, useMemo } from 'react';
import { useGetLocalFolderPathMutation } from '@frameio/shared/integrations/gql';
import { Button2 } from '@frameio/vapor';
import { IconEdit } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/LocalCopyRow';
import { SettingRow } from './SettingRow';
import { SettingRowCollapsible } from './SettingRowCollapsible';

TEXT.DEFINE({
  /** Label for saving a local copy */
  saveLocalCopy: 'Save a Local Copy',
  /** Label for the local save location */
  saveLocation: 'Save Location',
  /** Label when no location is selected */
  selectLocation: 'Select Location',
});

export const LocalCopyRow = ({
  enabled,
  folderPath,
  onEnabledChange,
  onFolderPathChange,
}: {
  /** Whether local copy is enabled. */
  enabled: boolean;
  /** Currently selected local folder path. */
  folderPath?: string | null;
  /** Called when the toggle state changes. Also prompts for a folder if enabling with no path set. */
  onEnabledChange: (enabled: boolean, folderPath?: string | null) => void;
  /** Called when the user selects a new local folder path. */
  onFolderPathChange: (path: string | null) => void;
}) => {
  const [getLocalFolderPath] = useGetLocalFolderPathMutation({
    context: { resolveWithUXP: true },
  });

  const handleToggle = useCallback(
    async (checked: boolean) => {
      onEnabledChange(checked);

      if (checked && !folderPath) {
        const result = await getLocalFolderPath();
        const path = result.data?.getLocalFolderPath ?? null;
        if (path !== null) {
          onEnabledChange(checked, path);
        }
      }
    },
    [getLocalFolderPath, folderPath, onEnabledChange]
  );

  const handleSelectFolder = useCallback(async () => {
    const result = await getLocalFolderPath();
    const path = result.data?.getLocalFolderPath ?? null;
    onFolderPathChange(path);
  }, [getLocalFolderPath, onFolderPathChange]);

  const folderName = useMemo(
    () => (folderPath ? folderPath.split('/').slice(-1)[0] : null),
    [folderPath]
  );

  return (
    <SettingRowCollapsible
      checked={enabled}
      label={TEXT.saveLocalCopy}
      onChange={handleToggle}
    >
      <SettingRow
        field={
          <Button2
            block={true}
            iconAfter={<IconEdit />}
            onPress={handleSelectFolder}
            spaceBetween={true}
          >
            {folderName || TEXT.selectLocation}
          </Button2>
        }
        title={TEXT.saveLocation}
      />
    </SettingRowCollapsible>
  );
};
